import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { cardTerminalApi, storefrontAdminApi } from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';

/**
 * Public Store → Card payment → Dojo configuration (hosted checkout).
 * Same credentials as Settings → Card machines; terminal ID stays there.
 */
export default function DojoStoreSettingsTab({ embedded = false }: { embedded?: boolean }) {
  const qc = useQueryClient();
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['dojoSettings'],
    queryFn: async () => (await cardTerminalApi.getDojoSettings()).data.message,
  });

  const { data: storeSettings } = useQuery({
    queryKey: ['storefrontAdminSettings'],
    queryFn: async () => (await storefrontAdminApi.getSettings()).data.message,
  });

  const [form, setForm] = useState({
    enabled: false,
    currency: 'GBP',
    api_key: '',
    software_house_id: '',
    reseller_id: '',
    webhook_secret: '',
  });

  useEffect(() => {
    if (!settings) return;
    const storeOn = Number(storeSettings?.pay_online_dojo) === 1;
    const credsOn = Number(settings.enabled) === 1 || settings.enabled === true;
    const hostedOn = Number(settings.enable_hosted_checkout) !== 0;
    setForm({
      enabled: storeOn || (credsOn && hostedOn),
      currency: settings.currency || 'GBP',
      api_key: '',
      software_house_id: settings.software_house_id || '',
      reseller_id: settings.reseller_id || '',
      webhook_secret: '',
    });
  }, [settings, storeSettings?.pay_online_dojo]);

  const saveMutation = useMutation({
    mutationFn: async (payload: typeof form) => {
      const enabled = embedded ? true : Boolean(payload.enabled);
      await cardTerminalApi.saveDojoSettings({
        enabled: enabled ? 1 : 0,
        enable_hosted_checkout: enabled ? 1 : 0,
        sync_storefront: 1,
        currency: payload.currency,
        api_key: payload.api_key || undefined,
        software_house_id: payload.software_house_id,
        reseller_id: payload.reseller_id,
        webhook_secret: payload.webhook_secret || undefined,
        // Preserve terminal fields from Card machines
        terminal_id: settings?.terminal_id || undefined,
      });
      await storefrontAdminApi.saveSettings({
        ...(storeSettings || {}),
        pay_online_dojo: enabled ? 1 : 0,
        pay_online_stripe: enabled ? 0 : storeSettings?.pay_online_stripe || 0,
        pay_online_teya: enabled ? 0 : storeSettings?.pay_online_teya || 0,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dojoSettings'] });
      qc.invalidateQueries({ queryKey: ['storefrontAdminSettings'] });
      qc.invalidateQueries({ queryKey: ['cardTerminalConfig'] });
      setForm((f) => ({ ...f, api_key: '', webhook_secret: '' }));
      toast.success('Dojo shop settings saved');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = (
        await cardTerminalApi.testDojo({
          api_key: form.api_key || undefined,
          software_house_id: form.software_house_id || undefined,
        })
      ).data.message;
      toast.success(res?.message || 'Connected to Dojo');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setTesting(false);
    }
  };

  const copyWebhook = () => {
    if (!settings?.webhook_url) return;
    void navigator.clipboard.writeText(settings.webhook_url);
    setCopiedWebhook(true);
    toast.success('Webhook URL copied');
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  const ready =
    (embedded || form.enabled) &&
    (Boolean(settings?.has_api_key) || Boolean(form.api_key));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-700 mr-2" />
        <span className="text-sm font-medium">Loading Dojo configuration…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {!embedded && (
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-2xl p-6 text-white shadow-md">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-indigo-500/20 text-indigo-200 border border-indigo-400/30">
              Online checkout
            </span>
            <span
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider border ${
                ready
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40'
                  : 'bg-gray-500/20 text-gray-400 border-gray-400/30'
              }`}
            >
              {ready ? (settings?.is_sandbox_key ? 'Sandbox' : 'Live') : 'Not ready'}
            </span>
          </div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-indigo-300" />
            Dojo Shop Integration
          </h2>
          <p className="text-xs text-indigo-100/80 mt-1 max-w-xl">
            Accept card payments on your public store via Dojo Prebuilt Checkout. POS card machines stay
            under Settings → Card machines.
          </p>
        </div>
      )}

      <div
        className={`flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm ${
          ready ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'
        }`}
      >
        {ready ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
        {ready
          ? 'Customers can choose Pay online (Dojo) at checkout.'
          : 'Save a Dojo API key so the store can offer card checkout.'}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMutation.mutate(form);
        }}
        className="space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-3">
            <label className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-amber-500" />
              Environment
            </label>
            <p className="text-sm text-slate-600">
              {settings?.is_sandbox_key === true
                ? 'Sandbox key detected (sk_sandbox_…)'
                : settings?.is_sandbox_key === false
                  ? 'Live key detected (sk_prod_…)'
                  : 'Environment is determined by your API key prefix.'}
            </p>
            <p className="text-[11px] text-slate-500">
              Get keys from{' '}
              <a
                href="https://developer.dojo.tech"
                target="_blank"
                rel="noreferrer"
                className="underline inline-flex items-center gap-1"
              >
                Dojo Developer Portal <ExternalLink className="w-3 h-3" />
              </a>
              .
            </p>
          </div>
          <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-3">
            <label className="text-xs font-bold text-gray-900 uppercase tracking-wider">Currency</label>
            <select
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm"
            >
              <option value="GBP">GBP — British Pound</option>
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — US Dollar</option>
            </select>
            <p className="text-[11px] text-slate-500">
              Need a physical card machine? Configure it in{' '}
              <Link to="/settings?tab=card_machines" className="underline font-medium text-slate-800">
                Settings → Card machines
              </Link>
              .
            </p>
          </div>
        </div>

        <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-slate-900">API credentials</h3>
          <label className="block text-sm space-y-1">
            <span className="text-slate-600">
              API Key {settings?.has_api_key ? <span className="text-emerald-600">(saved)</span> : null}
            </span>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 pr-10 font-mono text-xs"
                placeholder={settings?.has_api_key ? 'Leave blank to keep current key' : 'sk_sandbox_… or sk_prod_…'}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                onClick={() => setShowKey((v) => !v)}
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>
          <label className="block text-sm space-y-1">
            <span className="text-slate-600">Software House ID</span>
            <input
              value={form.software_house_id}
              onChange={(e) => setForm({ ...form, software_house_id: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
            />
          </label>
          <label className="block text-sm space-y-1">
            <span className="text-slate-600">Reseller ID (optional)</span>
            <input
              value={form.reseller_id}
              onChange={(e) => setForm({ ...form, reseller_id: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
            />
          </label>
          <label className="block text-sm space-y-1">
            <span className="text-slate-600">
              Webhook Secret{' '}
              {settings?.has_webhook_secret ? <span className="text-emerald-600">(saved)</span> : null}
            </span>
            <input
              type="password"
              value={form.webhook_secret}
              onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
              placeholder={settings?.has_webhook_secret ? 'Leave blank to keep current secret' : 'From Dojo webhook subscription'}
              autoComplete="new-password"
            />
          </label>

          {settings?.webhook_url ? (
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Webhook URL
              </div>
              <div className="flex items-center gap-2">
                <code className="text-[11px] text-slate-700 break-all flex-1">{settings.webhook_url}</code>
                <button
                  type="button"
                  onClick={copyWebhook}
                  className="p-2 rounded-lg hover:bg-white border border-slate-200"
                >
                  {copiedWebhook ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Subscribe to <code>payment_intent.status_updated</code> in the Dojo Developer Portal.
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handleTest()}
            disabled={testing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Test Dojo connection
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-700 text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Save Dojo configuration
          </button>
        </div>
      </form>
    </div>
  );
}
